import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
    this.setAttribute('initialized', '');
    this.#syncCollectionPageMetadata();
  }

  #handleCollectionUpdate = async (event) => {
    if (!event.promise || typeof event.promise.then !== 'function') return;
    try {
      await event.promise;
    } catch {
      return;
    }
    if (this.isConnected) this.#syncCollectionPageMetadata();
  };

  #syncCollectionPageMetadata() {
    const payload = this.querySelector('template[data-bf-collection-page]');
    if (!(payload instanceof HTMLTemplateElement) || window.Shopify?.designMode) return;

    const title = payload.dataset.bfDocumentTitle;
    const canonicalValue = payload.dataset.bfCanonicalUrl;
    if (!title || !canonicalValue) return;

    let canonical;
    try {
      canonical = new URL(canonicalValue, window.location.href);
    } catch {
      return;
    }

    const current = new URL(window.location.href);
    if (canonical.origin !== current.origin || canonical.pathname !== current.pathname) return;
    if ((canonical.searchParams.get('page') || '1') !== (current.searchParams.get('page') || '1')) return;

    const canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (!(canonicalLink instanceof HTMLLinkElement)) return;
    const oldCanonical = canonicalLink.href;
    if (document.title !== title) document.title = title;
    if (canonicalLink.href !== canonical.href) canonicalLink.href = canonical.href;

    const openGraphUrl = document.head.querySelector('meta[property="og:url"]');
    if (openGraphUrl instanceof HTMLMetaElement && openGraphUrl.content !== canonical.href) {
      openGraphUrl.content = canonical.href;
    }

    const canonicalPage = canonical.searchParams.get('page');
    const oldPage = new URL(oldCanonical).searchParams.get('page');
    for (const alternate of document.head.querySelectorAll('link[rel="alternate"][hreflang]')) {
      if (!(alternate instanceof HTMLLinkElement)) continue;
      const url = new URL(alternate.href);
      if (!url.pathname.includes('/collections/') || url.searchParams.get('page') !== oldPage) continue;
      if (canonicalPage) url.searchParams.set('page', canonicalPage);
      else url.searchParams.delete('page');
      if (alternate.href !== url.href) alternate.href = url.href;
    }

    for (const [rel, value] of [['prev', payload.dataset.bfPreviousUrl], ['next', payload.dataset.bfNextUrl]]) {
      const existing = document.head.querySelector(`link[rel="${rel}"]`);
      if (!value) {
        existing?.remove();
        continue;
      }
      let url;
      try {
        url = new URL(value, current);
      } catch {
        continue;
      }
      if (url.origin !== current.origin || url.pathname !== current.pathname) continue;
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      const link = existing || document.createElement('link');
      if (!(link instanceof HTMLLinkElement)) continue;
      link.rel = rel;
      if (link.href !== url.href) link.href = url.href;
      if (!existing) document.head.append(link);
    }

    const breadcrumb = this.closest('main')?.querySelector('nav.bf-breadcrumbs')?.nextElementSibling;
    if (!(breadcrumb instanceof HTMLScriptElement) || breadcrumb.type !== 'application/ld+json') return;
    try {
      const data = JSON.parse(breadcrumb.textContent || 'null');
      if (data?.['@type'] !== 'BreadcrumbList' || data['@id'] !== `${oldCanonical}#breadcrumb`) return;
      if (!Array.isArray(data.itemListElement) || data.itemListElement.length < 2) return;
      const last = data.itemListElement.at(-1);
      if (last?.item !== oldCanonical || oldCanonical === canonical.href) return;
      data['@id'] = `${canonical.href}#breadcrumb`;
      last.item = canonical.href;
      breadcrumb.textContent = JSON.stringify(data);
    } catch {
      return;
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
